/* =========================================================
   VIVID — Cart (Lines) Controller v12.0 (Phase 1: The Engine)
   - Architecture: One-Pass Fetch (Bundles Lines + Footer).
   - Sync: Updates Header & Mobile Menu badges directly from Data.
   - Performance: Zero Layout Thrashing (Updates DOM in one frame).
   ========================================================= */

(() => {
  const SECTION_LINES = "vh-cart-lines";
  const SECTION_FOOTER = "vh-cart-footer";
  const SECTION_HEADER = "vh-header";
  
  const instances = new Map();
  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  /* --- Helpers: Layout & Metrics --- */
  function getHeaderOffset() {
    const header = document.querySelector('[role="banner"]') || document.querySelector("header");
    const safe = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--vh-safe-top"), 10);
    return (header?.offsetHeight || 0) + (isNaN(safe) ? 0 : safe);
  }

  function applyPadSettings(root) {
    const padTop = root.getAttribute("data-pad-top") || '64';
    const padBottom = root.getAttribute("data-pad-bottom") || '36';
    
    root.style.setProperty("--vh-cart-pad-top", `${parseInt(padTop, 10)}px`);
    root.style.setProperty("--vh-cart-pad-bottom", `${parseInt(padBottom, 10)}px`);
    root.style.setProperty("--vh-cart-header-offset", `${getHeaderOffset()}px`);
  }

  /* --- Core: Data Fetching --- */
  async function fetchCartData() {
    const res = await fetch("/cart.js");
    if (!res.ok) throw new Error("Cart data failed");
    return res.json();
  }

  async function fetchSectionsHTML(footerId) {
    const sections = [SECTION_LINES];
    if (footerId) sections.push(footerId);

    const res = await fetch(`${window.location.pathname}?sections=${sections.join(",")}&t=${Date.now()}`);
    if (!res.ok) throw new Error("HTML failed");
    return res.json();
  }

  /* --- Core: DOM Updates --- */
  function updateHeaderBadges(count) {
    // 1. Update the Header State Attribute
    const header = $(`[data-section="${SECTION_HEADER}"]`);
    if (header) header.setAttribute("data-cart-count", count);

    // 2. Update all Badges (Desktop & Mobile)
    const badges = $$('[data-vh-cart-badge]');
    const display = count > 99 ? '99+' : count;

    badges.forEach(el => {
      el.textContent = display;
      if (count === 0) el.setAttribute("hidden", "");
      else el.removeAttribute("hidden");
    });
  }

  function swapSectionHTML(container, html) {
    if (!container || !html) return;
    
    // Create temp node to parse the HTML string
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    // A. Preferred: Swap .js-contents (Preserves wrapper classes/padding)
    const incoming = tmp.querySelector(".js-contents");
    const current  = container.querySelector(".js-contents");

    if (incoming && current) {
      current.replaceWith(incoming);
      return;
    }

    // B. Fallback: Swap innerHTML of the container
    // We avoid swapping the container itself to keep event listeners alive if they are bound to root
    container.innerHTML = tmp.firstElementChild?.innerHTML || html;
  }

  /* --- Logic: The Master Refresh --- */
  async function refreshAll(scope) {
    try {
      // 1. Identify Footer ID (Dynamic)
      const footerRoot = $(`[data-section="${SECTION_FOOTER}"]`);
      const footerId = footerRoot?.getAttribute("data-id");

      // 2. Fetch Data & HTML in Parallel (Fastest)
      const [cart, sections] = await Promise.all([
        fetchCartData(),
        fetchSectionsHTML(footerId)
      ]);

      // 3. Batch DOM Updates (Prevents Jumps)
      requestAnimationFrame(() => {
        // A. Update Lines
        const linesRoot = $(`[data-section="${SECTION_LINES}"]`);
        if (linesRoot && sections[SECTION_LINES]) {
           swapSectionHTML(linesRoot, sections[SECTION_LINES]);
           // Re-bind events to new DOM elements
           init(linesRoot);
        }

        // B. Update Footer
        if (footerRoot && footerId && sections[footerId]) {
          swapSectionHTML(footerRoot, sections[footerId]);
        }

        // C. Update Header Count
        updateHeaderBadges(cart.item_count);

        // D. Handle Empty States
        const isEmpty = cart.item_count === 0;
        
        if (linesRoot) linesRoot.classList.toggle('is-empty', isEmpty);
        if (footerRoot) footerRoot.classList.toggle('is-empty', isEmpty);

        // E. Scroll Logic (Smart)
        if (isEmpty) {
           // If emptied, scroll up slightly so they see the "Empty" message
           linesRoot?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });

    } catch (err) {
      console.error("VIVID Cart Sync Error:", err);
    }
  }

  /* --- Actions --- */
  async function changeLine(scope, { line, quantity }) {
    showError(scope, line, ""); // Clear errors
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error();
      await refreshAll(scope);
    } catch (e) {
      showError(scope, line, "Update failed.");
    }
  }

  async function addUpsell(scope, formData) {
    try {
      const res = await fetch("/cart/add.js", { method: "POST", body: formData });
      if (!res.ok) throw new Error();
      await refreshAll(scope);
    } catch (e) {
      console.warn("Upsell failed");
    }
  }

  /* --- UI Utilities --- */
  function showError(scope, lineIndex, message) {
    const wrap = scope.querySelector(`#Line-item-error-${lineIndex}`);
    if (!wrap) return;
    const slot = wrap.querySelector(".cart-item__error-text");
    if (slot) slot.textContent = message || "";
    wrap.classList.toggle("cart-item__error--show", !!message);
  }

  function parseIndex(input) {
    return parseInt(input.dataset.index || input.getAttribute("data-index") || "0", 10);
  }

  /* --- Event Listeners --- */
  function bindQty(scope) {
    // Delegation allows us to bind once to the root, even if contents swap
    if (scope.dataset.vhBoundQty) return; 
    
    scope.addEventListener("click", (e) => {
      const btn = e.target.closest(".quantity__button");
      if (!btn) return;
      const input = btn.closest(".quantity")?.querySelector(".quantity__input");
      if (!input) return;
      
      const line = parseIndex(input);
      const step = parseInt(input.step || "1", 10);
      let next = parseInt(input.value || "0", 10) || 0;

      if (btn.name === "minus") next = Math.max(0, next - step);
      if (btn.name === "plus")  next = next + step;

      if (next !== parseInt(input.value)) {
        input.value = String(next);
        changeLine(scope, { line, quantity: next });
      }
    });

    scope.addEventListener("change", (e) => {
      const input = e.target.closest(".quantity__input");
      if (!input) return;
      changeLine(scope, { line: parseIndex(input), quantity: parseInt(input.value || 0, 10) });
    });

    scope.dataset.vhBoundQty = "true";
  }

  function bindRemove(scope) {
    if (scope.dataset.vhBoundRemove) return;
    scope.addEventListener("click", (e) => {
      const btn = e.target.closest(".vh-remove, cart-remove-button");
      if (!btn) return;
      e.preventDefault();
      
      // Try to find input in the same row
      const row = btn.closest(".vh-row");
      const input = row?.querySelector(".quantity__input");
      const line = input ? parseIndex(input) : parseInt(btn.dataset.index || "0", 10);
      
      if (line) changeLine(scope, { line, quantity: 0 });
    });
    scope.dataset.vhBoundRemove = "true";
  }

  function bindUpsell(scope) {
      const forms = scope.querySelectorAll('.vh-upsell-form');
      forms.forEach(form => {
        if(form.dataset.vhBound) return;
        form.dataset.vhBound = "true";

        // 1. Handle Standard Submit (Enter key, etc)
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const fd = new FormData(form);
          addUpsell(scope, fd);
        });

        // 2. FORCE CLICK: Make the specific vh-cta trigger the submit
        const trigger = form.querySelector('.vh-cta');
        if (trigger) {
          trigger.style.cursor = "pointer"; // UI feedback
          trigger.addEventListener('click', (e) => {
            e.preventDefault(); // Stop default link behavior
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
        }
      });
    }

  function init(root) {
    const el = (root instanceof Element && root.classList.contains("vh-cart-lines")) 
      ? root 
      : $(`[data-section="${SECTION_LINES}"]`, root);

    if (!el) return;

    applyPadSettings(el);
    bindQty(el);
    bindRemove(el);
    bindUpsell(el);
    
    // Clear errors on load
    $$(".cart-item__error--show", el).forEach(e => e.classList.remove("cart-item__error--show"));
  }

  /* --- Lifecycle --- */
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init(document));
  else init(document);

  document.addEventListener("shopify:section:load", (e) => {
    if (e.target?.dataset?.section === SECTION_LINES) init(e.target);
  });

})();